import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n } from 'react-redux-i18n';
import * as settingActions from '../../../actions/settingActions';
import * as loadingBarActions from '../../../actions/loadingBarActions';
import './networkSetting.scss';
import PolicyTheGlobalNetwork from '../../../components/gsn/PolicyTheGlobalNetwork';
// Components
import AffiliateNetworkSettings from './components/AffiliateNetworkSettings';
import MarkupJobs from './components/MarkupJobs';
import AffiliateRates from './components/AffiliateRates';
import AddOns from './components/AddOns';
import CarTypeMapping from './components/CarTypeMapping';

// API
import {
  getAffiliateCarTypeApi,
  getFleetAffiliationCarTypeApi,
  getFleetNetworkAffiliationApi,
  saveFleetNetworkAffiliationApi,
} from '../../../constants/ApiConfigs';
import { callApiWithoutDispatch } from '../../../utils/apiUtils';
import PropTypes from 'prop-types';
import CcCheckbox from '../../../components/ccCheckbox/CcCheckbox';
import { filterTransportCarTypes } from '../../../utils/commonFunctions';

// ============================================================================
// INITIAL STATE
// ============================================================================

const INITIAL_FLEET_NETWORK_SETTINGS = {
  affiliateNetwork: {
    enable: false,
    rateTemplateId: '',
    addonTemplateId: '',
  },
  markup: {
    fleetValue: 0,
  },
};

const INITIAL_CAR_TYPE_MAPPING_STATE = {
  affiliationCarTypes: [], // List từ getAffiliateCarTypeApi (global)
  mappings: {}, // { [affiliationCarTypeId]: fleetCarTypeId }
  isLoading: false,
};

const INITIAL_OPTIONS = {
  rateTemplate: [],
  addOn: [],
  carTypeMapping: [], // Internal car types (VehicleType)
};

// ============================================================================
// COMPONENT
// ============================================================================

const NetworkSetting = (props, context) => {
  const { selectedFleet } = props.auth || {};
  const fleetId = selectedFleet?.fleetId;

  // --------------------------------------------------------------------------
  // STATE
  // --------------------------------------------------------------------------

  const [showPolicyTheGlobalNetwork, setShowPolicyTheGlobalNetwork] = useState(false);
  const [fleetNetworkSettings, setFleetNetworkSettings] = useState(
    INITIAL_FLEET_NETWORK_SETTINGS,
  );
  const [carTypeMappingState, setCarTypeMappingState] = useState(
    INITIAL_CAR_TYPE_MAPPING_STATE,
  );
  const [options, setOptions] = useState(INITIAL_OPTIONS);
  const [confirmSave, setConfirmSave] = useState(false);

  // --------------------------------------------------------------------------
  // DATA LOADING
  // --------------------------------------------------------------------------

  /**
   * Load fleet network settings từ API
   */
  const loadFleetNetworkSettings = async () => {
    try {
      const response = await callApiWithoutDispatch(
        getFleetNetworkAffiliationApi,
        {
          method: 'GET',
          params: { fleetId },
        },
      );

      if (response?.res) {
        setFleetNetworkSettings(response.res);
      }
    } catch (error) {
      console.error('Error loading fleet network settings:', error);
    }
  };

  /**
   * Load car type mapping data (AffiliationCarTypes + FleetAffiliationCarType)
   */
  const loadCarTypeMappingData = async () => {
    if (!fleetId) return;

    setCarTypeMappingState((prev) => ({ ...prev, isLoading: true }));

    try {
      const [affiliationCarTypesRes, fleetMappingsRes] = await Promise.all([
        callApiWithoutDispatch(getAffiliateCarTypeApi, { method: 'GET' }),
        callApiWithoutDispatch(getFleetAffiliationCarTypeApi, {
          method: 'GET',
          params: { fleetId },
        }),
      ]);

      const affiliationCarTypes =
        affiliationCarTypesRes?.res || affiliationCarTypesRes || [];
      const fleetMappings = fleetMappingsRes?.res || fleetMappingsRes || [];

      // Convert array to object: { affiliationCarTypeId: fleetCarTypeId }
      const mappings = {};
      if (Array.isArray(fleetMappings)) {
        fleetMappings.forEach((item) => {
          if (item.affiliationCarTypeId) {
            mappings[item.affiliationCarTypeId] = item.fleetCarTypeId || '';
          }
        });
      }

      setCarTypeMappingState({
        affiliationCarTypes: Array.isArray(affiliationCarTypes)
          ? affiliationCarTypes
          : [],
        mappings,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error loading car type mapping data:', error);
      setCarTypeMappingState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  /**
   * Load dropdown options (rate templates, add-ons, internal car types)
   */
  const loadDropdownOptions = async () => {
    const params = {
      limit: 100,
      page: 0,
      query: { fleetId },
    };

    try {
      const [rateTemplateRes, addOnRes, carTypeRes] = await Promise.all([
        props.settingActions.rateTemplateFind({ ...params, fleetId }),
        props.settingActions.addonTemplateFind({ ...params, fleetId }),
        props.settingActions.carTypeFindNormal(params),
      ]);

      setOptions({
        rateTemplate: rateTemplateRes?.res?.list || [],
        addOn: addOnRes?.res?.list || [],
        carTypeMapping: carTypeRes?.res?.list || carTypeRes?.res || [],
      });
    } catch (error) {
      console.error('Error loading dropdown options:', error);
    }
  };

  // Load all data on mount
  useEffect(() => {
    if (!fleetId) return;

    loadFleetNetworkSettings();
    loadDropdownOptions();
    loadCarTypeMappingData();
  }, [fleetId]);

  // --------------------------------------------------------------------------
  // HANDLERS - Fleet Network Settings
  // --------------------------------------------------------------------------

  const handleToggleEnable = (params = {}) => {
    setFleetNetworkSettings((prev) => ({
      ...prev,
      affiliateNetwork: {
        ...prev.affiliateNetwork,
        enable: params ? params.enable : !prev.affiliateNetwork?.enable,
      },
      farmOut: {
        ...prev.farmOut,
        isActive: params ? params.enable : !prev.affiliateNetwork?.enable,
      },
      farmIn: {
        ...prev.farmIn,
        isActive: params ? params.enable : !prev.affiliateNetwork?.enable,
      },
    }));
  };

  const handleToggleFarmOut = () => {
    setFleetNetworkSettings((prev) => ({
      ...prev,
      farmOut: {
        ...prev.farmOut,
        isActive: !prev.farmOut?.isActive,
      },
    }));
  };

  const handleToggleFarmIn = () => {
    setFleetNetworkSettings((prev) => ({
      ...prev,
      farmIn: {
        ...prev.farmIn,
        isActive: !prev.farmIn?.isActive,
      },
    }));
  };
  const handleMarkupChange = (e) => {
    setFleetNetworkSettings((prev) => ({
      ...prev,
      markup: {
        ...prev.markup,
        fleetValue: e.target.value,
      },
    }));
  };

  const handleRateTemplateChange = (e) => {
    setFleetNetworkSettings((prev) => ({
      ...prev,
      affiliateNetwork: {
        ...prev.affiliateNetwork,
        rateTemplateId: e.target.value,
      },
    }));
  };

  const handleAddOnChange = (e) => {
    setFleetNetworkSettings((prev) => ({
      ...prev,
      affiliateNetwork: {
        ...prev.affiliateNetwork,
        addonTemplateId: e.target.value,
      },
    }));
  };

  // --------------------------------------------------------------------------
  // HANDLERS - Car Type Mapping
  // --------------------------------------------------------------------------

  const handleCarTypeMappingChange = (affiliationCarTypeId, fleetCarTypeId) => {
    setCarTypeMappingState((prev) => ({
      ...prev,
      mappings: {
        ...prev.mappings,
        [affiliationCarTypeId]: fleetCarTypeId,
      },
    }));
  };

  // --------------------------------------------------------------------------
  // HELPER FUNCTIONS
  // --------------------------------------------------------------------------

  /**
   * Tìm internal car type theo ID
   */
  const getSelectedCarType = (carTypeId) => {
    return options.carTypeMapping.find(
      (car) => car._id === carTypeId || car.id === carTypeId,
    );
  };

  /**
   * Kiểm tra Insufficient Capacity
   * Warning khi internal car type có capacity nhỏ hơn affiliate car type
   */
  const checkCapacityWarning = (affiliationCarType, internalCarType) => {
    if (!internalCarType || !affiliationCarType) return false;

    const affMaxPassengers = affiliationCarType.maxPassengers || 0;
    const affMaxLuggages = affiliationCarType.maxLuggages || 0;
    const intMaxPassengers = internalCarType.maxPassengers || 0;
    const intMaxLuggages = internalCarType.maxLuggages || 0;

    return (
      intMaxPassengers < affMaxPassengers || intMaxLuggages < affMaxLuggages
    );
  };

  // --------------------------------------------------------------------------
  // MEMOIZED OPTIONS (for dropdowns)
  // --------------------------------------------------------------------------

  const rateTemplateOptions = useMemo(
    () =>
      options.rateTemplate.map((t) => ({
        value: t._id || t.id,
        label: t.name || I18n.t('networkSetting.Default'),
      })),
    [options.rateTemplate],
  );

  const addOnOptions = useMemo(
    () =>
      options.addOn.map((a) => ({
        value: a._id || a.id,
        label: a.name || I18n.t('networkSetting.Default'),
      })),
    [options.addOn],
  );

  const carTypeMappingOptions = useMemo(
    () =>
      filterTransportCarTypes(options.carTypeMapping))?.map((ct) => ({
        value: ct._id || ct.id,
        label: ct.appDisplayName || '',
        carType: ct,
      }),
    [options.carTypeMapping],
  );

  // --------------------------------------------------------------------------
  // SAVE
  // --------------------------------------------------------------------------

  /**
   * Chuẩn bị data car type mapping để save
   * Convert { affiliationCarTypeId: fleetCarTypeId } thành array documents
   */
  const prepareCarTypeMappingForSave = () => {
    return Object.entries(carTypeMappingState.mappings).map(
      ([affiliationCarTypeId, fleetCarTypeId]) => ({
        fleetId,
        affiliationCarTypeId,
        fleetCarTypeId: fleetCarTypeId || '',
      }),
    );
  };

  /**
   * Chuẩn bị fleet network settings để save
   */
  const prepareFleetNetworkForSave = () => ({
    affiliateNetwork: {
      enable: fleetNetworkSettings.affiliateNetwork?.enable || false,
      rateTemplateId:
        fleetNetworkSettings.affiliateNetwork?.rateTemplateId || '',
      addonTemplateId:
        fleetNetworkSettings.affiliateNetwork?.addonTemplateId || '',
    },
    markup: {
      fleetValue: Number(fleetNetworkSettings.markup?.fleetValue) || 0,
    },
    farmOut: {
      isActive: fleetNetworkSettings.farmOut?.isActive || false,
    },
    farmIn: {
      isActive: fleetNetworkSettings.farmIn?.isActive || false,
    },
  });

  const handleSave = async () => {
    try {
      const payload = {
        fleetId,
        fleetAffiliationCarType: prepareCarTypeMappingForSave(),
        fleetNetwork: prepareFleetNetworkForSave(),
      };

      props.loadingBarActions.showLoadingSpiner();
      const result = await callApiWithoutDispatch(
        saveFleetNetworkAffiliationApi,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      );

      // Update state với response từ server
      const { fleet } = result?.res || {};
      props.loadingBarActions.hideLoadingSpiner();
      if (fleet) {
        context.notification('success', I18n.t('networkSetting.SaveSuccess'));
        setFleetNetworkSettings(fleet);
        window.location.reload();
      }

      // Reload car type mapping data
      loadCarTypeMappingData();

      // TODO: Show success notification
    } catch (error) {
      props.loadingBarActions.hideLoadingSpiner();
      context.notification('error', I18n.t('networkSetting.SaveError'));
    }
  };


  useEffect(() => {
    if (confirmSave) {
      handleSave();
      setConfirmSave(false);
    }
  }, [confirmSave]);

  return (
    <>
      <PolicyTheGlobalNetwork
        isShow={showPolicyTheGlobalNetwork}
        onClose={() => setShowPolicyTheGlobalNetwork(false)}
        onConfirm={async () => {
          setShowPolicyTheGlobalNetwork(false);
          handleToggleEnable({ enable: true });
          setConfirmSave(true);
        }}
      />
      <div className="network-setting-container">
        {/* Enable/Disable Affiliate Network */}
        <AffiliateNetworkSettings
          isEnable={fleetNetworkSettings.affiliateNetwork?.enable}
          onToggle={() => {
            if (fleetNetworkSettings.affiliateNetwork?.enable) {
              handleToggleEnable({ enable: false });
            } else {
              setShowPolicyTheGlobalNetwork(!showPolicyTheGlobalNetwork)
            }
          }}
        />
        {fleetNetworkSettings.affiliateNetwork?.enable && (
          <>
            <div className="network-setting-section">
              <h3 className="section-title">
                {I18n.t('Sidebar.Settings.Network_Settings')}
              </h3>
              <div className="input-field-wrapper">
                <div
                  className="checkbox-container"
                  style={{ display: 'flex', gap: '20px' }}
                >
                  <CcCheckbox
                    checked={fleetNetworkSettings.farmOut?.isActive}
                    onChange={handleToggleFarmOut}
                    text={I18n.t('networkSetting.farmOutSend')}
                  />
                  <CcCheckbox
                    checked={fleetNetworkSettings.farmIn?.isActive}
                    onChange={handleToggleFarmIn}
                    text={I18n.t('networkSetting.farmInReceive')}
                  />
                </div>
              </div>
            </div>

            {/* Markup Settings */}
            <MarkupJobs
              markup={fleetNetworkSettings.markup?.fleetValue}
              onChange={handleMarkupChange}
            />

            {/* Rate Template Selection */}
            <AffiliateRates
              rateTemplate={
                fleetNetworkSettings.affiliateNetwork?.rateTemplateId
              }
              rateTemplateOptions={rateTemplateOptions}
              onChange={handleRateTemplateChange}
            />

            {/* Add-On Selection */}
            <AddOns
              addOn={fleetNetworkSettings.affiliateNetwork?.addonTemplateId}
              addOnOptions={addOnOptions}
              onChange={handleAddOnChange}
            />

            {/* Car Type Mapping */}
            <CarTypeMapping
              affiliationCarTypes={carTypeMappingState.affiliationCarTypes}
              mappings={carTypeMappingState.mappings}
              carTypeOptions={carTypeMappingOptions}
              onChange={handleCarTypeMappingChange}
              getSelectedCarType={getSelectedCarType}
              checkCapacityWarning={checkCapacityWarning}
              isLoading={carTypeMappingState.isLoading}
            />
          </>
        )}

        {/* Save Button */}
        <div className="save-button-container">
          <button className="save-button" onClick={handleSave}>
            {I18n.t('networkSetting.Save')}
          </button>
        </div>
      </div>
    </>
  );
};

NetworkSetting.contextTypes = {
  notification: PropTypes.func,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

const mapDispatchToProps = (dispatch) => ({
  settingActions: bindActionCreators(settingActions, dispatch),
  loadingBarActions: bindActionCreators(loadingBarActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(NetworkSetting);
